import { useContext, useEffect, useState } from "react";
import { Tabs, Tab, Box } from "@mui/material";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Collection } from "../../types/collections.types";
import { useWindowWidth } from "../../utils/useWindowWith";
import { CollectionAndFieldsPermission } from "../../api/useCollections";
import { RecordContext } from "../record/Record";
import { useUser } from "../../api/useAuth";
import { MENU_WIDTH } from "../menu/Menu";

type TabPanelProps = {
    children?: React.ReactNode;
    index: number;
    value: number;
};

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <Box
            role="tabpanel"
            id={`simple-tabpanel-${index}`}
            {...other}
            sx={{
                display: value === index ? "flex" : "none", // adjust here
            }}
        >
            {value === index && (
                <Box display="flex" flex={1} flexDirection="column">
                    {children}
                </Box>
            )}
        </Box>
    );
}

type DetailTabsProps = {
    tabs: Array<{
        label: string;
        icon: React.ReactElement;
        entity: string;
        filter?: (context: { item?: any; userRole?: string }) => boolean;
    }>;
};

export function DetailTabs({ tabs }: DetailTabsProps) {

    const location = useLocation();
    const [value, setValue] = useState<number | 0>(0);
    const windowWidth = useWindowWidth();
    const recordContext = useContext(RecordContext);
    const { data: user } = useUser();
    const userRole = user?.role.id;

    const queryClient = useQueryClient();
    const collectionsWithPermission = queryClient.getQueryData(["collections"]) as CollectionAndFieldsPermission;

    const allowedTabs = tabs.filter((tab) => {
        if (tab.filter) {
            const filterContext = {
                item: recordContext?.item,
                userRole: userRole,
                userId: user?.id,
            };
            return tab.filter(filterContext);
        }

        return (
            tab.entity === "detail" ||
            tab.entity === "timeline" ||
            tab.entity === "eeg_analysis" ||
            tab.entity === "psychology" ||
            tab.entity === "logbook" ||
            tab.entity === "responses" ||
            collectionsWithPermission?.collections?.some((collection: Collection) => collection.collection === tab.entity)
        );
    });

    const navigate = useNavigate();

    useEffect(() => {
        const currentTab = location.pathname.split("/").pop();
        const initialValue = allowedTabs.findIndex((tab) => tab.entity === currentTab);

        if (initialValue === -1 && allowedTabs.length > 0) {
            navigate(allowedTabs[0].entity);
            setValue(0);
        } else if (initialValue !== value) {
            setValue(initialValue);
        }
    }, [location.pathname, allowedTabs, navigate, value]);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        navigate(allowedTabs[newValue].entity);
        setValue(newValue);
    };

    const scrollbarOffset = 33;
    const tabWidthPixelBased = windowWidth - MENU_WIDTH - scrollbarOffset;

    // TODO on treatments the previous styles, have a blank space problem with the header when there isnt scrollbar
    // the new doesnt work yet
    const tabContainerStyles = {
        previous: { width: { xs: "100vw", sm: tabWidthPixelBased } },
        new: { maxWidth: "100%", overflowX: "hidden" as const },
    };

    const tabContentStyles = {
        previous: { width: { xs: "100vw", sm: tabWidthPixelBased } },
        new: { width: "100%" },
    };

    return (
        <Box display="flex" flexDirection="column" sx={tabContainerStyles.previous}>
            <Tabs
                value={allowedTabs.length === 1 ? 0 : value}
                onChange={handleChange}
                sx={{
                    borderTopLeftRadius: 8,
                    "& .MuiTabs-scrollButtons.Mui-disabled": { opacity: 0.3 },
                    "& .MuiTabs-scroller": {
                        overflowX: { xs: "scroll", sm: "hidden" },
                        WebkitOverflowScrolling: "touch",
                        scrollbarWidth: "thin",
                        scrollbarColor: "#bdbdbd transparent",
                    },
                }}
                variant="scrollable"
                scrollButtons="auto"
            >
                {allowedTabs.map((tab) => (
                    <Tab icon={tab.icon} iconPosition="start" key={tab.label} label={tab.label} />
                ))}
            </Tabs>

            {allowedTabs.map((tab, index) => (
                <TabPanel value={value} index={index} key={tab.label}>
                    <Box sx={tabContentStyles.previous} >
                        <Outlet />
                    </Box>
                </TabPanel>
            ))}
        </Box>
    );
}
